import { AccountingRemittance } from "@/core/entities/accounting/AccountingRemittance";
import { AccountingJournalEntry } from "@/core/entities/accounting/AccountingJournalEntry";
import { AccountingSystemInvoice } from "@/core/entities/accounting/AccountingSystemInvoice";
import { AccountingSystemInvoicesGroupedData } from "@/core/repositories/accounting";
import type { ICreateJournalEntryResponse } from "@/core/repositories/accounting";
import { Deduction } from "@/core/entities/deduction/deduction";
import { BrandMember } from "@/core/entities/lookup/brandMember";
import { IDeductionColumns } from "@/core/repositories/deduction/IDeduction.repository";
import { AppConfig } from "@/infrastructure/services/config";
import { FilterDateRange } from "@/presentation/deduction/components/DeductionFilter/interfaces";
import { SelectOption, TableSortInfoColumnType } from "@ir/ui";
import { atom } from "jotai";

const _AppConfig = AppConfig.getInstance();
const PAGINATION_PAGE_LIMIT = _AppConfig.PAGINATION_PAGE_LIMIT;

type Option = SelectOption;

export interface AccountingRemittanceFilterInfo {
  lastFilteredAt: Date | null;
  search: string | null;
  checkFromDate: string | null;
  checkToDate: string | null;
  checkDateRange: FilterDateRange | null;
  purchaser: Option[];
  sortColumn: string | null;
  sortOrder: TableSortInfoColumnType["order"] | null;
}

export interface AccountingDataFilterInfo {
  lastFilteredAt: Date | null;
  search: string | null;
  checkFromDate: string | null;
  checkToDate: string | null;
  checkDateRange: FilterDateRange | null;
  invoiceFromDate: string | null;
  invoiceToDate: string | null;
  invoiceDateRange: FilterDateRange | null;
  purchaser: Option[];
  dc: Option[];
  glCode: Option[];
  analyst: Option[];
  approver: Option[];
  sortColumn: string | null;
  sortOrder: TableSortInfoColumnType["order"] | null;
}

interface LinkedInvoiceItem {
  externalInvoiceId: number;
  externalInvoiceDocumentNumber: string;
  linkedAmount: number;
}

export interface AccountingLinkedDeduction {
  deductionId: number;
  deductionClaimNumber: string;
  deductionCheckNumber: string;
  deductionNetAmount: number;
  externalInvoiceTransactionDate: Date;
  externalInvoiceDescription: string;
  linkedInvoices: LinkedInvoiceItem[];
}

export interface AccountingState {
  loggedInBrandMember: BrandMember | null;
  selectedAccountingDataItems: Deduction[];
  selectedAccountingDataItem: Deduction | null;
  accountingDataItems: Deduction[];
  isFilterApplied: boolean;
  userHasClearedAccountingFilters: boolean;
  filterInfo: AccountingDataFilterInfo;
  columns: IDeductionColumns[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  totalCount: number;
  currentPage: number;
  pageLimit: number;
  accountingSystemInvoices: AccountingSystemInvoice[];
  groupedInvoicesByCacheKey: Record<string, AccountingSystemInvoicesGroupedData>;
  activeGroupedInvoicesCacheKey: string | null;
  isLoadingGroupedInvoices: boolean;
  selectedAccountingSystemInvoices: AccountingSystemInvoice[];
  accountingSystemKey: string | null;
  accountingLinkedDeductions: AccountingLinkedDeduction[];
  accountingRemittances: AccountingRemittance[];
  remittanceFilterInfo: AccountingRemittanceFilterInfo;
  isRemittanceFilterApplied: boolean;
  remittanceColumns: IDeductionColumns[];
  innerColumns: IDeductionColumns[];
  expandedRemittanceId: number | null;
  selectedAccountingRemittances: AccountingRemittance[];
  journalEntries: AccountingJournalEntry[];
  jeColumns: IDeductionColumns[];
  jeInnerColumns: IDeductionColumns[];
  jeIsLoading: boolean;
  jeIsRefreshing: boolean;
  jeError: Error | null;
  jeTotalCount: number;
  jeCurrentPage: number;
  jePageLimit: number;
  jeFilterInfo: AccountingRemittanceFilterInfo;
  jeIsFilterApplied: boolean;
  expandedJeRemittanceId: number | null;
  jeResult: ICreateJournalEntryResponse | null;
  isJeResultModalOpen: boolean;
}

const emptyJeFilterInfo: AccountingRemittanceFilterInfo = {
  lastFilteredAt: null,
  search: null,
  checkFromDate: null,
  checkToDate: null,
  checkDateRange: null,
  purchaser: [],
  sortColumn: null,
  sortOrder: null,
};

const initialState: AccountingState = {
  loggedInBrandMember: null,
  selectedAccountingDataItems: [],
  selectedAccountingDataItem: null,
  accountingDataItems: [],
  isFilterApplied: false,
  userHasClearedAccountingFilters: false,
  columns: [],
  isLoading: false,
  isRefreshing: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageLimit: PAGINATION_PAGE_LIMIT,
  accountingSystemInvoices: [],
  groupedInvoicesByCacheKey: {},
  activeGroupedInvoicesCacheKey: null,
  isLoadingGroupedInvoices: false,
  selectedAccountingSystemInvoices: [],
  accountingSystemKey: null,
  accountingLinkedDeductions: [],
  accountingRemittances: [],
  isRemittanceFilterApplied: false,
  remittanceColumns: [],
  innerColumns: [],
  expandedRemittanceId: null,
  selectedAccountingRemittances: [],
  remittanceFilterInfo: {
    lastFilteredAt: null,
    search: null,
    checkFromDate: null,
    checkToDate: null,
    checkDateRange: null,
    purchaser: [],
    sortColumn: null,
    sortOrder: null,
  },
  filterInfo: {
    lastFilteredAt: null,
    search: null,
    checkFromDate: null,
    checkToDate: null,
    checkDateRange: null,
    invoiceFromDate: null,
    invoiceToDate: null,
    invoiceDateRange: null,
    purchaser: [],
    dc: [],
    glCode: [],
    analyst: [],
    approver: [],
    sortColumn: null,
    sortOrder: null,
  },
  journalEntries: [],
  jeColumns: [],
  jeInnerColumns: [],
  jeIsLoading: false,
  jeIsRefreshing: false,
  jeError: null,
  jeTotalCount: 0,
  jeCurrentPage: 1,
  jePageLimit: PAGINATION_PAGE_LIMIT,
  jeFilterInfo: emptyJeFilterInfo,
  jeIsFilterApplied: false,
  expandedJeRemittanceId: null,
  jeResult: null,
  isJeResultModalOpen: false,
};

export const accountingAtom = atom<AccountingState>(initialState);
export const loggedInBrandMemberAtom = atom(get => get(accountingAtom).loggedInBrandMember);
export const accountingDataAtom = atom(get => get(accountingAtom).accountingDataItems);
export const selectedAccountingDataItemsAtom = atom(get => get(accountingAtom).selectedAccountingDataItems);
export const selectedAccountingDataItemAtom = atom(get => get(accountingAtom).selectedAccountingDataItem);
export const filterInfoAtom = atom(get => get(accountingAtom).filterInfo);
export const isFilterAppliedAtom = atom(get => get(accountingAtom).isFilterApplied);
export const userHasClearedAccountingFiltersAtom = atom(get => get(accountingAtom).userHasClearedAccountingFilters);
export const columnsAtom = atom(get => get(accountingAtom).columns);
export const isLoadingAtom = atom(get => get(accountingAtom).isLoading);
export const isRefreshingAtom = atom(get => get(accountingAtom).isRefreshing);
export const errorAtom = atom(get => get(accountingAtom).error);
export const totalCountAtom = atom(get => get(accountingAtom).totalCount);
export const currentPageAtom = atom(get => get(accountingAtom).currentPage);
export const pageLimitAtom = atom(get => get(accountingAtom).pageLimit);
export const accountingSystemInvoicesAtom = atom(get => get(accountingAtom).accountingSystemInvoices);
export const accountingSystemInvoicesGroupedAtom = atom(get => {
  const state = get(accountingAtom);
  if (!state.activeGroupedInvoicesCacheKey) {
    return null;
  }
  return state.groupedInvoicesByCacheKey[state.activeGroupedInvoicesCacheKey] ?? null;
});
export const activeGroupedInvoicesCacheKeyAtom = atom(get => get(accountingAtom).activeGroupedInvoicesCacheKey);
export const groupedInvoicesCacheKeyAtom = activeGroupedInvoicesCacheKeyAtom;
export const isLoadingGroupedInvoicesAtom = atom(get => get(accountingAtom).isLoadingGroupedInvoices);
export const selectedAccountingSystemInvoicesAtom = atom(get => get(accountingAtom).selectedAccountingSystemInvoices);
export const accountingSystemKeyAtom = atom(get => get(accountingAtom).accountingSystemKey);
export const accountingLinkedDeductionsAtom = atom(get => get(accountingAtom).accountingLinkedDeductions);
export const accountingRemittancesAtom = atom(get => get(accountingAtom).accountingRemittances);
export const remittanceFilterInfoAtom = atom(get => get(accountingAtom).remittanceFilterInfo);
export const isRemittanceFilterAppliedAtom = atom(get => get(accountingAtom).isRemittanceFilterApplied);
export const remittanceColumnsAtom = atom(get => get(accountingAtom).remittanceColumns);
export const innerColumnsAtom = atom(get => get(accountingAtom).innerColumns);
export const expandedRemittanceIdAtom = atom(get => get(accountingAtom).expandedRemittanceId);
export const selectedAccountingRemittancesAtom = atom(get => get(accountingAtom).selectedAccountingRemittances);
export const journalEntriesAtom = atom(get => get(accountingAtom).journalEntries);
export const jeColumnsAtom = atom(get => get(accountingAtom).jeColumns);
export const jeInnerColumnsAtom = atom(get => get(accountingAtom).jeInnerColumns);
export const jeIsLoadingAtom = atom(get => get(accountingAtom).jeIsLoading);
export const jeIsRefreshingAtom = atom(get => get(accountingAtom).jeIsRefreshing);
export const jeErrorAtom = atom(get => get(accountingAtom).jeError);
export const jeTotalCountAtom = atom(get => get(accountingAtom).jeTotalCount);
export const jeCurrentPageAtom = atom(get => get(accountingAtom).jeCurrentPage);
export const jePageLimitAtom = atom(get => get(accountingAtom).jePageLimit);
export const jeFilterInfoAtom = atom(get => get(accountingAtom).jeFilterInfo);
export const jeIsFilterAppliedAtom = atom(get => get(accountingAtom).jeIsFilterApplied);
export const expandedJeRemittanceIdAtom = atom(get => get(accountingAtom).expandedJeRemittanceId);

export const jeResultAtom = atom(get => get(accountingAtom).jeResult);
export const isJeResultModalOpenAtom = atom(get => get(accountingAtom).isJeResultModalOpen);
